/**
 * Live Orders & Trades Table Reconciliation Models.
 *
 * Formats Flattrade broker order rows, audit trail reconciliation,
 * order history modals, and stable trade fill keys.
 */

import { tickAlign } from "./pricing";
import { escapeHtml } from "../ui/html_utils";

/**
 * AUD-10 — one broker value as a JavaScript string argument inside a
 * double-quoted HTML attribute: JSON.stringify makes it a valid, fully-quoted JS
 * string (quotes, backslashes, newlines escaped), escapeHtml then makes that
 * safe inside the attribute. The order book carries orders placed from OTHER
 * platforms on the account, so none of its fields is ours to trust.
 */
function jsArg(v: unknown): string {
  return escapeHtml(JSON.stringify(v == null ? "" : String(v)));
}

export const CANCELLABLE_STATUSES = ["OPEN", "PENDING", "TRIGGER_PENDING"] as const;

export interface TableCellModel {
  text?: string | number;
  html?: string;
}

export interface TableRowModel {
  key: string;
  cls: string;
  cells: TableCellModel[];
}

export interface OrderHistoryRecord {
  time: string;
  date: string;
  qty: number;
  remaining: string | number;
  filled: number;
  avgPrice: string;
  status: string;
}

/**
 * Builds table rows reconciling broker order records with audit log entries.
 */
export function buildOrderRowModels(
  brokerRows: any[],
  auditRows: any[],
): TableRowModel[] {
  const byBroker: Record<string, any> = {};
  if (Array.isArray(auditRows)) {
    for (const a of auditRows) {
      if (a && a.broker_order_id) {
        byBroker[a.broker_order_id] = a;
      }
    }
  }

  return (brokerRows || []).map(o => {
    const audit = o && o.norenordno ? byBroker[o.norenordno] : undefined;
    const st = ((o && o.status) || "").toUpperCase();
    const shown =
      audit && audit.status === "CANCEL_REQUESTED" && st === "OPEN"
        ? "CANCELLING…"
        : st;

    let action = `<a class="ordno" href="javascript:void(0)" onclick="orderHistory(${jsArg(o.norenordno)})">#${escapeHtml(o.norenordno)}</a>`;
    if ((CANCELLABLE_STATUSES as readonly string[]).includes(st) && shown !== "CANCELLING…") {
      const call = audit
        ? `cancelOrder(${Number(audit.id)})`
        : `cancelBroker(${jsArg(o.norenordno)})`;
      action =
        `<button class="btn-edit" onclick="modifyOrder(${[o.norenordno, o.exch, o.tsym, o.prc || "", o.qty || "", o.trgprc || ""].map(jsArg).join(",")})">Edit</button> ` +
        `<button class="btn-cancel" onclick="${call}">Cancel</button> ` +
        action;
    }

    const prcAvgText = `${o.prc ?? "-"}${o.avgprc && parseFloat(o.avgprc) ? " / " + o.avgprc : ""}`;
    const statusText = `${shown}${o.rejreason && o.rejreason.trim() ? " — " + o.rejreason : ""}`;

    return {
      key: String(o.norenordno || ""),
      cls: "",
      cells: [
        { text: (o.norentm || "").slice(0, 8) },
        { text: (o.tsym || "") + (audit ? "" : " ●") },
        { text: o.prctyp || "" },
        { text: o.trantype === "B" ? "BUY" : "SELL" },
        { text: o.qty || 0 },
        { text: `${o.trgprc ?? "-"}` },
        { text: prcAvgText },
        { text: o.prd || "" },
        { text: statusText },
        { html: action },
      ],
    };
  });
}

/**
 * Builds table rows with stable per-fill keys for live trades.
 */
export function buildTradeRowModels(trades: any[]): TableRowModel[] {
  const counts: Record<string, number> = {};

  return (trades || []).map(t => {
    const base = `${t.norenordno || ""}|${t.norentm || ""}|${t.flprc || ""}|${t.flqty || t.qty || ""}`;
    const n = (counts[base] = (counts[base] || 0) + 1);
    const key = n > 1 ? `${base}#${n}` : base;

    return {
      key,
      cls: "",
      cells: [
        { text: (t.norentm || "").slice(0, 8) },
        { text: t.tsym || "" },
        { text: t.trantype === "B" ? "BUY" : "SELL" },
        { text: t.flqty || t.qty || 0 },
        { text: t.flprc || "-" },
        { text: t.prd || "" },
        { text: `#${t.norenordno || ""}` },
      ],
    };
  });
}

/**
 * Formats audit order history records for modal display.
 */
export function formatOrderHistoryRows(history: any[]): OrderHistoryRecord[] {
  return (history || []).map(h => {
    const qty = parseInt(h.qty || 0, 10);
    const fill = parseInt(h.fillshares || 0, 10);
    const t = (h.norentm || "").split(" ");
    const remaining = isNaN(qty - fill) ? "-" : qty - fill;
    const avgPrice = h.avgprc && parseFloat(h.avgprc) ? h.avgprc : "0";
    const status = `${(h.status || "").toUpperCase()}${h.rejreason && h.rejreason.trim() ? " — " + h.rejreason : ""}`;

    return {
      time: t[1] || "",
      date: t[0] || "",
      qty,
      remaining,
      filled: fill,
      avgPrice,
      status,
    };
  });
}

export interface ModifyOrderParams {
  exchange: string;
  tradingsymbol: string;
  price?: number | string | null;
  qty?: number | string | null;
  triggerPrice?: number | string | null;
  tick?: number;
}

/**
 * Builds tick-aligned JSON payload for broker order modifications.
 */
export function buildModifyOrderPayload(params: ModifyOrderParams): Record<string, any> {
  const tick = params.tick || 0.05;
  const body: Record<string, any> = {
    exchange: params.exchange,
    tradingsymbol: params.tradingsymbol,
  };

  const np =
    typeof params.price === "number"
      ? params.price
      : parseFloat(String(params.price || ""));
  if (!isNaN(np) && np > 0) {
    body.price = +(Math.round(np / tick) * tick).toFixed(2);
  }

  const nq =
    typeof params.qty === "number"
      ? params.qty
      : parseInt(String(params.qty || ""), 10);
  if (!isNaN(nq) && nq > 0) {
    body.qty = nq;
  }

  if (params.triggerPrice != null && params.triggerPrice !== "") {
    const ntrg =
      typeof params.triggerPrice === "number"
        ? params.triggerPrice
        : parseFloat(String(params.triggerPrice));
    if (!isNaN(ntrg) && ntrg > 0) {
      body.trigger_price = tickAlign(ntrg, params.tick);
    }
  }

  return body;
}

/**
 * Builds HTML markup for the order modification modal.
 */
export function buildModifyOrderModalHtml(
  no: string | number,
  exch: string,
  tsym: string,
  prc: number | string,
  qty: number | string,
  trg?: number | string,
): string {
  const hasTrigger = trg != null && parseFloat(String(trg)) > 0;
  return `<div class="ncm-card acc-accent" style="max-width:380px;">
      <div class="ncm-title">MODIFY ORDER #${no}</div>
      <div class="ncm-sub">${tsym} (${exch})</div>
      <div style="display:flex;gap:10px;margin-bottom:14px;">
        <div style="flex:1;"><label style="font-size:10px;">PRICE ₹</label>
          <input id="m-price" type="number" step="0.05" value="${prc}"></div>
        <div style="flex:1;"><label style="font-size:10px;">QTY</label>
          <input id="m-qty" type="number" value="${qty}"></div>
        ${hasTrigger ? `<div style="flex:1;"><label style="font-size:10px;">TRIGGER ₹</label>
          <input id="m-trg" type="number" step="0.05" value="${trg}"></div>` : ""}
      </div>
      <div class="ncm-foot">
        <button id="m-no" class="ncm-btn ghost">Cancel</button>
        <button id="m-yes" class="ncm-btn go">MODIFY REAL ORDER</button>
      </div></div>`;
}

export interface OrderConfirmParams {
  side: string;
  qty: number;
  price: number;
  tsym: string;
  exch?: string;
  marginLine?: string;
}

/**
 * Builds HTML markup for the live order placement confirmation modal.
 */
export function buildOrderConfirmModalHtml(params: OrderConfirmParams): string {
  const approxVal = (params.qty * params.price).toLocaleString("en-IN");
  return `<div class="ncm-card acc-red" style="max-width:430px;text-align:center;">
        <div class="ncm-title center">⚠ REAL MONEY ORDER — FLAT TRADE</div>
        <div style="font-size:14px;line-height:1.8;"><b>${params.side}</b> ${params.qty} × <b>${params.tsym}</b> (${params.exch || "NFO"})<br>
        Limit price: <b>₹${params.price}</b> · ≈ value ₹${approxVal}${params.marginLine || ""}</div>
        <div class="ncm-foot" style="justify-content:center;">
          <button id="cm-no" class="ncm-btn ghost">Cancel</button>
          <button id="cm-yes" class="ncm-btn go">PLACE REAL ORDER</button>
        </div></div>`;
}

export interface SubmitOrderPayloadParams {
  symbol?: string;
  exchange: string;
  tradingsymbol: string;
  side: string;
  product: string;
  order_type: string;
  qty: number;
  price: number;
  trigger?: number | string | null;
  tick?: number;
  clientOrderId: string;
}

/**
 * Builds JSON payload for broker order placement, tick-aligning trigger price if present.
 */
export function buildSubmitOrderPayload(
  params: SubmitOrderPayloadParams,
): Record<string, any> {
  const body: Record<string, any> = {
    symbol: params.symbol,
    exchange: params.exchange,
    tradingsymbol: params.tradingsymbol,
    side: params.side,
    product: params.product,
    order_type: params.order_type,
    qty: params.qty,
    price: params.price,
    client_order_id: params.clientOrderId,
  };

  if (params.trigger != null && params.trigger !== "") {
    const ntrg =
      typeof params.trigger === "number"
        ? params.trigger
        : parseFloat(String(params.trigger));
    if (!isNaN(ntrg) && ntrg > 0) {
      body.trigger_price = tickAlign(ntrg, params.tick);
    }
  }

  return body;
}

/**
 * Formats user-facing toast string after broker order response.
 */
export function formatOrderSuccessToast(
  data: { status: string; split?: boolean; orders?: any[] },
  side: string,
  qty: number,
  tsym: string,
  price: number | string,
): string {
  const splitText =
    data.split && Array.isArray(data.orders)
      ? ` (split ×${data.orders.length})`
      : "";
  return `${(data.status || "ORDER").toUpperCase()}: ${side} ${qty} ${tsym} @ ${price}${splitText}`;
}

